<!-- src/components/engineering/CreateSupportRequestModal.vue -->
<template>
  <div class="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center" @click.self="closeModal">
    <div class="relative mx-auto p-6 md:p-8 border w-full max-w-lg shadow-lg rounded-md bg-white">
      
      <div class="flex justify-between items-center border-b pb-3 mb-6">
        <h3 class="text-xl font-semibold text-gray-800">{{ formTitle }}</h3>
        <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
          <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <form @submit.prevent="handleSave" class="space-y-4">
        <div>
          <label for="companyName" class="form-label">Company Display Name <span class="text-red-500">*</span></label>
          <input type="text" id="companyName" v-model="formData.companyDisplayName" required class="form-input" placeholder="e.g., Company Name">
        </div>

        <div>
          <label for="companyLogo" class="form-label">Company Logo</label>
          <img v-if="existingLogoUrl" :src="existingLogoUrl" alt="Current Logo" class="h-16 w-auto mb-2 border p-1 rounded-md"/>
          <p v-if="existingLogoUrl" class="text-xs text-gray-500 mb-2">Uploading a new logo will replace the current one.</p>
          <input type="file" id="companyLogo" @change="handleFileChange" accept="image/png, image/jpeg" class="form-input file-input">
        </div>
        
        <div class="pt-4 flex justify-end space-x-2">
          <button @click="closeModal" type="button" class="btn-secondary-action">
            Cancel
          </button>
          <button type="submit" :disabled="isSubmitting" class="btn-primary-action">
            <span v-if="isSubmitting">{{ isEditing ? 'Saving...' : 'Creating...' }}</span>
            <span v-else>{{ isEditing ? 'Save Changes' : 'Create Form' }}</span>
          </button>
        </div>
        <p v-if="error" class="text-sm text-red-600 mt-2 text-center">{{ error }}</p>
      </form>

    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, onMounted } from 'vue';
import { useUserStore } from '@/stores/userStore';
import { firebaseApp } from '@/firebaseConfig'; 
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";

const props = defineProps({
  template: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['close']);
const userStore = useUserStore();
const functions = getFunctions(firebaseApp, 'europe-west2');
const storage = getStorage();

const isEditing = computed(() => !!props.template);
const formTitle = computed(() => isEditing.value ? 'Edit Support Request Template' : 'Create New Support Request Template');

const formData = reactive({
  companyDisplayName: '',
  logoFile: null,
});
const existingLogoUrl = ref('');

const isSubmitting = ref(false);
const error = ref('');

onMounted(() => {
  if (isEditing.value) {
    formData.companyDisplayName = props.template.companyDisplayName;
    existingLogoUrl.value = props.template.companyLogoUrl;
  }
});

const closeModal = () => { emit('close'); };

const handleFileChange = (event) => {
  formData.logoFile = event.target.files[0] || null;
};

const generateSlug = (name) => {
  return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
};

const handleSave = async () => {
  if (!formData.companyDisplayName.trim()) {
    error.value = "Company Display Name is required.";
    return;
  }
  
  isSubmitting.value = true;
  error.value = '';

  try {
    const templateId = isEditing.value ? props.template.id : generateSlug(formData.companyDisplayName);
    let logoUrl = existingLogoUrl.value;

    if (formData.logoFile) {
      const logoPath = `company-logos/${templateId}-${formData.logoFile.name}`;
      const sRef = storageRef(storage, logoPath);
      await uploadBytes(sRef, formData.logoFile);
      logoUrl = await getDownloadURL(sRef);
    }

    const templateData = {
      clientId: userStore.claims.clientId,
      companyDisplayName: formData.companyDisplayName,
      companyLogoUrl: logoUrl,
      formTitle: "Support Request Form",
      status: "Active",
      fields: [
        // DEFINITIVE FIX: Hard-coded options are fully removed to ensure database is the single source of truth.
        // General Info
        { id: "region", label: "Region", mandatory: true, type: "dynamic-dropdown", optionsId: "region", gridSpan: 1 },
        { id: "discipline", label: "Discipline", mandatory: true, type: "dynamic-dropdown", optionsId: "discipline", gridSpan: 1 },
        { id: "sreContractRef", label: "SRE Contract Ref", mandatory: true, type: "text", gridSpan: 1 },
        { id: "requestorName", label: "Requestor Name", mandatory: true, type: "text", gridSpan: 1 },
        { id: "requestorEmail", label: "Requestor Email", mandatory: true, placeholder: "Enter your email", type: "text", gridSpan: 1 },
        { id: "requestorPhone", label: "Requestor Phone Number", mandatory: true, placeholder: "Enter your phone number", type: "tel", gridSpan: 1 },
        { id: "siteAddress", label: "Site Address", mandatory: true, type: "textarea", gridSpan: 2, placeholder: "Please add address or paste the What3Words" },
        { id: "workScope", label: "High Level Work Scope", mandatory: true, type: "textarea", gridSpan: 2 },
        { id: "elr", label: "ELR", mandatory: true, type: "text", gridSpan: 1 },
        { id: "tracksAffected", label: "Track(s) Affected", mandatory: true, type: "text", gridSpan: 1 },
        { id: "mileageFrom", label: "Mileage From", mandatory: true, type: "text", gridSpan: 1 },
        { id: "mileageTo", label: "Mileage To", mandatory: true, type: "text", gridSpan: 1 },
        
        // Shift 1
        { id: "shiftStartDate1", label: "Shift 1 Start Date/Time", mandatory: true, type: "datetime-local", gridSpan: 1 },
        { id: "shiftEndDate1", label: "Shift 1 End Date/Time", mandatory: true, type: "datetime-local", gridSpan: 1 },
        { id: "siteContactName1", label: "Shift 1 Site Contact Name", mandatory: true, type: "text", gridSpan: 1 },
        { id: "siteContactNumber1", label: "Shift 1 Site Contact Number", mandatory: true, type: "tel", gridSpan: 1 },
        { id: "resourceRequired1", label: "Shift 1 Resource Required", mandatory: true, type: "dynamic-dropdown", optionsId: "resourceRequired", gridSpan: 1 },
        { id: "level1", label: "Shift 1 Level", mandatory: true, type: "dropdown", options: ["1", "2", "3", "4", "5", "TBC"], condition: { field: "resourceRequired1", values: ["stresser", "track_hand_back"] }, gridSpan: 1 },
        { id: "quantity1", label: "Shift 1 Quantity", mandatory: true, type: "number", defaultValue: 1, gridSpan: 1 },
        { id: "equipment1", label: "Shift 1 Equipment", mandatory: true, type: "dropdown", options: ["Yes", "No"], gridSpan: 1 },
        { id: "equipmentDetails1", label: "Shift 1 Equipment Details", mandatory: true, type: "textarea", condition: { field: "equipment1", values: ["Yes"] }, gridSpan: 2 },
        
        // Shift 2
        { id: "shiftStartDate2", label: "Shift 2 Start Date/Time", mandatory: false, type: "datetime-local", gridSpan: 1 },
        { id: "shiftEndDate2", label: "Shift 2 End Date/Time", mandatory: false, type: "datetime-local", gridSpan: 1 },
        { id: "siteContactName2", label: "Shift 2 Site Contact Name", mandatory: false, type: "text", gridSpan: 1 },
        { id: "siteContactNumber2", label: "Shift 2 Site Contact Number", mandatory: false, type: "tel", gridSpan: 1 },
        { id: "resourceRequired2", label: "Shift 2 Resource Required", mandatory: false, type: "dynamic-dropdown", optionsId: "resourceRequired", gridSpan: 1 },
        { id: "level2", label: "Shift 2 Level", mandatory: false, type: "dropdown", options: ["1", "2", "3", "4", "5", "TBC"], condition: { field: "resourceRequired2", values: ["stresser", "track_hand_back"] }, gridSpan: 1 },
        { id: "quantity2", label: "Shift 2 Quantity", mandatory: false, type: "number", defaultValue: 1, gridSpan: 1 },
        { id: "equipment2", label: "Shift 2 Equipment", mandatory: false, type: "dropdown", options: ["Yes", "No"], gridSpan: 1 },
        { id: "equipmentDetails2", label: "Shift 2 Equipment Details", mandatory: false, type: "textarea", condition: { field: "equipment2", values: ["Yes"] }, gridSpan: 2 },
        
        // Shift 3
        { id: "shiftStartDate3", label: "Shift 3 Start Date/Time", mandatory: false, type: "datetime-local", gridSpan: 1 },
        { id: "shiftEndDate3", label: "Shift 3 End Date/Time", mandatory: false, type: "datetime-local", gridSpan: 1 },
        { id: "siteContactName3", label: "Shift 3 Site Contact Name", mandatory: false, type: "text", gridSpan: 1 },
        { id: "siteContactNumber3", label: "Shift 3 Site Contact Number", mandatory: false, type: "tel", gridSpan: 1 },
        { id: "resourceRequired3", label: "Shift 3 Resource Required", mandatory: false, type: "dynamic-dropdown", optionsId: "resourceRequired", gridSpan: 1 },
        { id: "level3", label: "Shift 3 Level", mandatory: false, type: "dropdown", options: ["1", "2", "3", "4", "5", "TBC"], condition: { field: "resourceRequired3", values: ["stresser", "track_hand_back"] }, gridSpan: 1 },
        { id: "quantity3", label: "Shift 3 Quantity", mandatory: false, type: "number", defaultValue: 1, gridSpan: 1 },
        { id: "equipment3", label: "Shift 3 Equipment", mandatory: false, type: "dropdown", options: ["Yes", "No"], gridSpan: 1 },
        { id: "equipmentDetails3", label: "Shift 3 Equipment Details", mandatory: false, type: "textarea", condition: { field: "equipment3", values: ["Yes"] }, gridSpan: 2 },
        
        // Other
        { id: "otherComments", label: "Other comments", mandatory: false, type: "textarea", gridSpan: 2 },
        { id: "documentUpload", label: "Upload Supporting Document", mandatory: false, type: "file", gridSpan: 2 }
      ]
    };

    const createOrUpdateTemplate = httpsCallable(functions, 'createOrUpdateFormTemplate');
    const result = await createOrUpdateTemplate({ templateId, templateData });
    
    console.log(result.data.message);
    closeModal();

  } catch (err) {
    error.value = `Failed to save form: ${err.message}`;
    console.error(err);
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.form-label { @apply block text-sm font-medium text-gray-700 mb-1; }
.form-input { @apply w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-brand-primary focus:border-brand-primary sm:text-sm; }
.file-input { @apply block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-brand-bg-light file:text-brand-primary hover:file:bg-brand-accent hover:file:text-white; }
.btn-primary-action { @apply bg-brand-primary text-white py-2 px-4 rounded-lg hover:bg-brand-accent transition-colors duration-200 font-medium shadow-sm disabled:opacity-50; }
.btn-secondary-action { @apply bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors duration-200 font-medium shadow-sm; }
</style>